import { useCallback, useEffect } from 'react';
import { toast } from '@/components/ui/use-toast';
import { REFETCH_TRADES_TAGS } from '@/constants';
import { promptDownloadFile } from '@/utils';
import { useEventLobbiesRoom, useScheduledThrottle } from '@/hooks';
import { authApi, useLazyGetLobbiesByEventIdForAdminQuery, gamesApi } from '@/store/api';
import { marketsApi } from '@/store/api/markets.api';
import { useAppDispatch } from '@/store';
import {
	ExportStatusEnum,
	ODDS_SERVICE_JOB_TYPE,
	TRADE_EXPORTED,
	JOIN_COMMON_UPDATES_ROOM,
	ON_COMMON_UPDATES,
	LEAVE_COMMON_UPDATES_ROOM,
	JOIN_COMMON_MARKETS_ROOM,
	ON_MARKETS_CHANGE,
	LEAVE_COMMON_MARKETS_ROOM,
	SLATE_SCOPE_UPDATED,
	MATCH_RESULT_UPDATED,
} from '@sportspredict/shared';

const MARKET_UPDATE_THROTTLE_DELAY = 3000;

export function useEventRealtimeSync({
	socket,
	eventId,
	event,
	setEvent,
	setLobbies,
	refetchLobbies,
	isProbability,
	hasLoadedTrades,
	isActiveTabTrades,
	scheduleRefetchTradeStat,
	scheduleRefetchTradeList,
	setShouldRefetchTrade,
	exportTradesJobId,
	setExportTradesJobId,
	exportedTradesFile,
	setExportedTradesFile,
	signedLinkData,
	setAvailableFutures,
	setFuturesMarkets,
	setIsLoadingFutures,
	setBulkMarkets,
	slatesRefetch,
}: {
	socket: any;
	eventId: any;
	event: any;
	setEvent: (updater: any) => void;
	setLobbies: (updater: any) => void;
	refetchLobbies: () => void;
	isProbability: boolean;
	hasLoadedTrades: boolean;
	isActiveTabTrades: boolean;
	scheduleRefetchTradeStat: () => void;
	scheduleRefetchTradeList: () => void;
	setShouldRefetchTrade: (value: boolean) => void;
	exportTradesJobId: any;
	setExportTradesJobId: (value: any) => void;
	exportedTradesFile: any;
	setExportedTradesFile: (value: any) => void;
	signedLinkData: any;
	setAvailableFutures: (value: any) => void;
	setFuturesMarkets: (value: any) => void;
	setIsLoadingFutures: (value: boolean) => void;
	setBulkMarkets: (updater: any) => void;
	slatesRefetch: () => void;
}) {
	const dispatch = useAppDispatch();

	const mergeLobbyUpdate = useCallback((currentLobby: any, incomingLobby: any) => {
		if (!currentLobby) return incomingLobby;
		if (!incomingLobby) return currentLobby;

		return {
			...currentLobby,
			...incomingLobby,
			lobby_users: incomingLobby.lobby_users ?? currentLobby.lobby_users,
			details: incomingLobby.details ?? currentLobby.details,
			content_rules: incomingLobby.content_rules ?? currentLobby.content_rules,
			eliminated_users: incomingLobby.eliminated_users ?? currentLobby.eliminated_users,
			revived_users: incomingLobby.revived_users ?? currentLobby.revived_users,
		};
	}, []);

	const refreshLobbies = useCallback(() => {
		if (!eventId) return;
		refetchLobbies();
	}, [eventId, refetchLobbies]);

	const currentEventListener = useCallback(
		(data: any) => {
			if (data?.lobby?.event_id === eventId) {
				setLobbies((prev: any[]) =>
					prev.some((l) => l.id === data.lobby.id)
						? prev.map((l) => (l.id === data.lobby.id ? mergeLobbyUpdate(l, data.lobby) : l))
						: [...prev, data.lobby]
				);
				void refreshLobbies();
			}

			if (data?.event?.id === eventId) {
				setEvent((prev: any) => (prev ? { ...prev, ...data.event } : data.event));
			}
		},
		[eventId, mergeLobbyUpdate, refreshLobbies, setLobbies, setEvent]
	);

	useEventLobbiesRoom({
		socket,
		enabled: Boolean(socket && eventId),
		handler: currentEventListener,
	});

	const invalidateMarkets = useCallback(() => {
		dispatch(marketsApi.util.invalidateTags([{ type: 'Market' as const, id: `LIST-${eventId}` }]));
	}, [dispatch, eventId]);

	const { schedule: scheduleMarketsInvalidate } = useScheduledThrottle(
		MARKET_UPDATE_THROTTLE_DELAY,
		invalidateMarkets
	);

	const handleDataBroadcast = useCallback(
		async (sockData: any) => {
			if (sockData?.eventId !== eventId || !sockData?.tag) {
				return;
			}
			if (sockData.tag === SLATE_SCOPE_UPDATED) {
				void slatesRefetch();
			}

			if (sockData.tag === MATCH_RESULT_UPDATED && sockData.match?.id) {
				dispatch(
					gamesApi.util.updateQueryData('getSlatesByEventId', { eventId }, (draft: any) => {
						for (const slate of draft) {
							const idx = slate.matches?.findIndex((m: any) => m.id === sockData.match.id);
							if (idx != null && idx !== -1) {
								slate.matches[idx] = { ...slate.matches[idx], ...sockData.match };
							}
						}
					})
				);
			}

			scheduleMarketsInvalidate();

			if (!isProbability && REFETCH_TRADES_TAGS.includes(sockData.tag) && hasLoadedTrades) {
				if (isActiveTabTrades) {
					scheduleRefetchTradeStat();
					scheduleRefetchTradeList();
				} else {
					setShouldRefetchTrade(true);
				}
			}

			if (sockData?.tag === TRADE_EXPORTED && sockData.jobId === exportTradesJobId) {
				if (sockData.status === ExportStatusEnum.COMPLETED) {
					toast({
						title: 'Success',
						description: `Exported trades to CSV`,
					});
					setExportedTradesFile(sockData.file);
					setExportTradesJobId(null);
				} else {
					toast({
						variant: 'destructive',
						title: 'Error',
						description: 'Failed to export trades',
					});
					setExportTradesJobId(null);
					setExportedTradesFile(null);
				}
			}
		},
		[
			eventId,
			exportTradesJobId,
			hasLoadedTrades,
			isActiveTabTrades,
			scheduleMarketsInvalidate,
			scheduleRefetchTradeList,
			scheduleRefetchTradeStat,
			slatesRefetch,
		]
	);

	const handleCommonUpdates = useCallback(
		async (sockData: any) => {
			const isEspnBatch = sockData?.createdMarkets?.some(
				(m: any) => m?.generation_source === 'espn'
			);
			const sourceTitle = isEspnBatch
				? 'Generate markets from ESPN'
				: 'Pull markets from OpticOdds';

			if (
				sockData?.type === 'NEW_MARKETS_BY_GAME_GENERATED' &&
				sockData?.createdMarketsCount === 0
			) {
				toast({
					title: sourceTitle,
					description:
						'At the moment, there are no new markets for this match or markets category.',
				});
				return;
			}
			if (
				sockData?.type === ODDS_SERVICE_JOB_TYPE.NEW_MARKETS_BY_GAME_GENERATED &&
				sockData?.status === 'ok' &&
				sockData?.createdMarketsCount
			) {
				const createdMarkets = sockData.createdMarkets;
				let uniqueMarketsCount = 0;
				setBulkMarkets((prevMarkets: any[]) => {
					const existingIds = new Set(
						prevMarkets.map((market: any) => market?.ext_api_market_id).filter(Boolean)
					);
					const uniqueNewMarkets = createdMarkets
						.filter(
							(market: any) =>
								!market?.ext_api_market_id || !existingIds.has(market.ext_api_market_id)
						)
						.map((market: any) => ({ ...market, isSelectedToCreate: false }));
					const filteredPrevMarkets = prevMarkets.filter((m: any) => m.question);
					uniqueMarketsCount = uniqueNewMarkets.length;
					return [...filteredPrevMarkets, ...uniqueNewMarkets];
				});
				toast({
					title: sourceTitle,
					description: uniqueMarketsCount
						? `Generated ${uniqueMarketsCount} new market(s)`
						: 'At the moment, there are no new markets for this match or markets category.',
				});
			}

			if (
				sockData?.type === ODDS_SERVICE_JOB_TYPE.REFRESHED_EVENT_MATCHES &&
				sockData?.data?.eventId === eventId &&
				sockData?.data?.updateCount > 0
			) {
				slatesRefetch();
			}

			if (
				sockData?.type === ODDS_SERVICE_JOB_TYPE.MATCH_RESULTS_REFRESHED &&
				sockData?.data?.eventId === eventId
			) {
				if (sockData.data.status === 'ok') {
					toast({ title: 'Success', description: 'Match results refreshed' });
					slatesRefetch();
				} else {
					toast({
						variant: 'destructive',
						title: 'Error',
						description: sockData.data.message || 'Failed to refresh match results',
					});
				}
			}

			// Futures socket handlers — only process updates for the current event
			if (sockData?.eventId && sockData.eventId !== eventId) return;

			if (
				sockData?.type === ODDS_SERVICE_JOB_TYPE.AVAILABLE_FUTURES_LISTED &&
				sockData?.status === 'ok' &&
				sockData?.categoryId === event?.category_id
			) {
				setAvailableFutures(sockData.futures || []);
				setIsLoadingFutures(false);
			}
			if (
				sockData?.type === ODDS_SERVICE_JOB_TYPE.AVAILABLE_FUTURES_LISTED &&
				sockData?.status === 'error'
			) {
				toast({
					variant: 'destructive',
					title: 'Error',
					description: sockData.message || 'Failed to load futures',
				});
				setIsLoadingFutures(false);
			}
			if (
				sockData?.type === ODDS_SERVICE_JOB_TYPE.FUTURES_MARKETS_GENERATED &&
				sockData?.status === 'ok'
			) {
				const generated = (sockData.createdMarkets || []).map((m: any, idx: number) => ({
					...m,
					isSelectedToCreate: false,
					idx,
				}));
				setFuturesMarkets(generated);
				setIsLoadingFutures(false);
				toast({
					title: 'Futures Markets',
					description: `Generated ${generated.length} market suggestion(s)`,
				});
			}
		},
		[eventId, event?.category_id]
	);

	const { data: profile } = authApi.endpoints.getProfile.useQueryState(undefined);
	const userId = profile?.id;

	useEffect(() => {
		if (!socket || !userId) return;
		socket.emit(JOIN_COMMON_UPDATES_ROOM, { userId });
		socket.on(ON_COMMON_UPDATES, handleCommonUpdates);
		return () => {
			socket.emit(LEAVE_COMMON_UPDATES_ROOM, { userId });
			socket.off(ON_COMMON_UPDATES, handleCommonUpdates);
		};
	}, [socket, userId, handleCommonUpdates]);

	useEffect(() => {
		if (!signedLinkData || !exportedTradesFile) return;
		if (signedLinkData.fileId !== exportedTradesFile.id) return;
		promptDownloadFile(signedLinkData.url, exportedTradesFile.fileName);

		setExportedTradesFile(null);
	}, [signedLinkData, exportedTradesFile]);

	useEffect(() => {
		if (!socket || !eventId) return;

		socket.emit(JOIN_COMMON_MARKETS_ROOM);
		socket.on(ON_MARKETS_CHANGE, handleDataBroadcast);

		return () => {
			socket.emit(LEAVE_COMMON_MARKETS_ROOM);
			socket.off(ON_MARKETS_CHANGE, handleDataBroadcast);
		};
	}, [socket, eventId, handleDataBroadcast]);
}
